import type {AppDispatch} from '@/store';
import type {MediaTypePublishingParams, SocialMediaAccountId, SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {openSocialAccountsUpsellDialog} from '@Libraries/social-media-account.library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {isPromotingEvent, trackEventPromotionPublishedForPlatform} from '@Libraries/social-media-event-promotion.library';
import {
  updatePublishedAccountPostUrlMapForAccountWithMediaType,
  updatePublishErrorMessageForAccount,
  updatePublishErrorMessageForAllParams,
  updatePublishingStatusForAccountWithMediaType,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {LoadingStates} from '@Utils/loading.util';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {trackFakeDoorPostPublishedIfNeeded} from '@Libraries/marketing-planner.library';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {ErrorResponseUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';
import {ErrorUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';

export interface HandlePublishSuccessResponseOpts {
  dispatch: AppDispatch;
  idSocialMediaAccount: SocialMediaAccountId;
  platformMediaType: PlatformMediaType;
  data: object;
  platform: SocialPlatform | undefined;
  onError: () => void;
}

export interface HandlePublishErrorResponseOpts {
  dispatch: AppDispatch;
  idSocialMediaAccount: SocialMediaAccountId;
  platformMediaType: PlatformMediaType;
  resetUrl: boolean;
}

/**
 * Finds the SocialPlatform for a given account id within the accounts list.
 * Returns undefined when no matching account is found.
 */
export const getPlatformForAccount = (accounts: SocialAccount[], id: SocialMediaAccountId): SocialPlatform | undefined => {
  return accounts.find((account) => account.id === id)?.type;
};

/**
 * Handles the success response from a publish API call.
 *
 * Dispatch sequence (mirrors the four publishing hooks):
 *  1. If `data.postUrl` present → dispatch published-URL update.
 *  2. If `data.publishErrorMessage` present → dispatch error-message update.
 *     When the message is truthy → call onError() and RETURN early (skip GA/LOADED).
 *  3. Fire GA POST_PUBLISHED event (platform string gets `_STORIES` suffix when
 *     platformMediaType is STORIES); fire promotion event when isPromotingEvent().
 *  4. Dispatch publishing status LOADED.
 */
export const handlePublishSuccessResponse = (opts: HandlePublishSuccessResponseOpts): void => {
  const {dispatch, idSocialMediaAccount, platformMediaType, data, platform, onError} = opts;

  if ('postUrl' in data && typeof data.postUrl === 'string') {
    dispatch(
      updatePublishedAccountPostUrlMapForAccountWithMediaType({
        accountId: idSocialMediaAccount,
        mediaType: platformMediaType,
        url: data.postUrl,
      })
    );
  }

  if ('publishErrorMessage' in data) {
    const errorMessage: string | null = typeof data.publishErrorMessage === 'string' ? data.publishErrorMessage : null;
    dispatch(
      updatePublishErrorMessageForAccount({
        idSocialMediaAccount,
        publishErrorMessage: errorMessage,
        platformMediaType,
      })
    );

    if (errorMessage) {
      onError();
      return;
    }
  }

  if (platform) {
    const platformString = getSocialPlatformName(platform);
    const platformNameForGA = platformMediaType === PlatformMediaType.STORIES ? `${platformString}_${PlatformMediaType.STORIES}` : platformString;
    trackPublishingGA4Events(GA4EventName.POST_PUBLISHED, {[GA4EventParamName.PLATFORM]: platformNameForGA});
    trackFakeDoorPostPublishedIfNeeded();

    if (isPromotingEvent()) {
      trackEventPromotionPublishedForPlatform(platformString);
    }
  }

  dispatch(
    updatePublishingStatusForAccountWithMediaType({
      idSocialMediaAccount,
      publishingStatus: LoadingStates.LOADED,
      platformMediaType,
    })
  );
};

/**
 * Handles the error response from a publish API call.
 *
 * Dispatch sequence (mirrors the four publishing hooks):
 *  1. When resetUrl is true → dispatch an empty published-URL reset (START hooks only).
 *  2. Dispatch publishing status NOT_LOADED.
 */
export const handlePublishErrorResponse = (opts: HandlePublishErrorResponseOpts): void => {
  const {dispatch, idSocialMediaAccount, platformMediaType, resetUrl} = opts;

  if (resetUrl) {
    dispatch(
      updatePublishedAccountPostUrlMapForAccountWithMediaType({
        accountId: idSocialMediaAccount,
        mediaType: platformMediaType,
        url: '',
      })
    );
  }

  dispatch(
    updatePublishingStatusForAccountWithMediaType({
      idSocialMediaAccount,
      publishingStatus: LoadingStates.NOT_LOADED,
      platformMediaType,
    })
  );
};

export interface ScheduleTrackingEvent {
  platformName: string;
  isStory: boolean;
}

/**
 * Returns the list of platform/story pairs to fire GA POST_SCHEDULED events for.
 * Returns an empty array when publishingAction is not SCHEDULE or when post is undefined.
 * Callers loop over the result and fire the GA + promotion events.
 */
export const buildScheduleTrackingEvents = (
  post: SocialPost | undefined,
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  publishingAction: SocialPostPublishingAction | undefined
): ScheduleTrackingEvent[] => {
  if (!post) {
    return [];
  }

  if (publishingAction !== SocialPostPublishingAction.SCHEDULE) {
    return [];
  }

  const events: ScheduleTrackingEvent[] = [];

  post.accounts.forEach((item) => {
    const platformName = getSocialPlatformName(item.type);
    const doesStoryExistForAccount = publishingParams[item.id].STORIES !== null;

    if (doesStoryExistForAccount) {
      events.push({platformName, isStory: true});
    }

    events.push({platformName, isStory: false});
  });

  return events;
};

export interface HandleUpdatePublishingParamsErrorOpts {
  error: ErrorResponseUpdatePublishingParams['error'];
  dispatch: AppDispatch;
  accountIdsToPublishTo: SocialMediaAccountId[];
}

const ERROR_CODES_CARRYING_AN_ACCOUNT_MESSAGE: readonly ErrorUpdatePublishingParams[] = [
  ErrorUpdatePublishingParams.RATE_LIMITED,
  ErrorUpdatePublishingParams.SOCIAL_ACCOUNTS_NEED_REAUTH,
];

/**
 * Dispatches the error-specific side-effects for a failed updateSocialMediaPostPublishingParams call.
 * Returns true when the caller should skip the remaining non-error-specific work
 * (i.e. when the error is ALREADY_PUBLISHED), false otherwise.
 */
export const handleUpdatePublishingParamsError = (opts: HandleUpdatePublishingParamsErrorOpts): boolean => {
  const {error, dispatch, accountIdsToPublishTo} = opts;

  if (ERROR_CODES_CARRYING_AN_ACCOUNT_MESSAGE.includes(error.code) && error.message !== '' && accountIdsToPublishTo.length > 0) {
    dispatch(updatePublishErrorMessageForAllParams(error.message));
  }

  if (error.code === ErrorUpdatePublishingParams.SOCIAL_ACCOUNT_PLAN_LOCKED) {
    openSocialAccountsUpsellDialog();
  }

  if (error.code === ErrorUpdatePublishingParams.ALREADY_PUBLISHED) {
    return true;
  }

  return false;
};
